import React, { useRef } from 'react';
import { AutoSizer } from 'react-virtualized';
import { useDispatch } from 'react-redux';
import { Add, PlayArrow } from '@mui/icons-material';
import { ReactComponent as DeleteIcon } from 'new/components/icons/fi-rr-trash.svg';
import { updateFormData } from 'js/actions';
import { TextField } from 'new/elements/TextField';
import { FORM_TYPE_CREATE_REUSABLE_TX } from 'js/constants/formTypes';
import { Tooltip } from '@mui/material';
import { TRANSFORM_RULES_PROTO_STR } from 'js/constants/schemaEditor';
import { TransformType } from 'new/components/ReusableTransform/types';
import { useDebouncedCallback } from 'new/hooks/useDebouncedCallback';
import { TransformCodeMonacoEditor } from 'js/components/elements/schema-editor/schema-inspector/transforms/TransformCodeMonacoEditor';
import SupportedLanguageButtons from '../components/SupportedLanguageButtons';

import { useSelectedEditorLanguage } from './useSelectedEditorLanguage';

type Props = {
  isCapsuleCluster?: boolean;
  isRecordType: boolean;
  code: string;
  isFetchingOutputSamples: boolean;
  onChangeCode: (code: string) => void;
  language: string;
  inputCode: string;
  transformType: TransformType;
  runTransform: () => void;
  argumentValuesVersion: number;
  argumentValues: Array<{ name: string; description: string }>;
  argumentsTestData: Array<{ fName: string; type: string; value: any }>;
};

function FunctionArgs(props: Props) {
  const {
    argumentValues,
    argumentsTestData,
    argumentValuesVersion,
    language,
    transformType,
    isCapsuleCluster = false,
  } = props;

  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);

  const { options } = useSelectedEditorLanguage(language, transformType, isCapsuleCluster);

  const onChangeCode = useDebouncedCallback(props.onChangeCode, [props.onChangeCode], 300);

  const addFunctionArgument = () => {
    const numItems = argumentValues.length;
    const newItem = { name: `arg-${numItems + 1}`, description: '' };

    const newTestItem = {
      fName: `arg-${numItems + 1}`,
      ...TRANSFORM_RULES_PROTO_STR,
      value: '',
    };

    dispatch(
      updateFormData(FORM_TYPE_CREATE_REUSABLE_TX, {
        txArgs: {
          v: argumentValuesVersion,
          items: [...argumentValues, newItem],
        },
        txArgsTestData: [...argumentsTestData, newTestItem],
      }),
    );
  };

  const deleteFunctionArgument = (idx = -1) => {
    if (argumentValues.length === 0) return;

    const newItems = [...argumentValues];
    newItems.splice(idx, 1);
    const newTestData = [...argumentsTestData];
    newTestData.splice(idx, 1);

    dispatch(
      updateFormData(FORM_TYPE_CREATE_REUSABLE_TX, {
        txArgs: { v: argumentValuesVersion, items: newItems },
        txArgsTestData: newTestData,
      }),
    );
  };

  const updateFunctionArgumentInput = (idx = -1, element: HTMLInputElement) => {
    if (argumentValues.length === 0) return;

    const inputName = element.name;
    const inputValue = element.value;

    let newItems = [...argumentValues];
    let item = newItems[idx];
    item[inputName] = inputValue;

    let newTestData = [...argumentsTestData];

    if (inputName === 'name') {
      let thisTestDataItem = newTestData[idx];
      thisTestDataItem.fName = inputValue;
    }

    dispatch(
      updateFormData(FORM_TYPE_CREATE_REUSABLE_TX, {
        txArgs: { v: argumentValuesVersion, items: newItems },
        txArgsTestData: newTestData,
      }),
    );
  };

  const onChangeField = (idx: number, event: { target: HTMLInputElement }) =>
    updateFunctionArgumentInput(idx, event.target);

  /**
   * calculating the minHeight based on number of args
   */
  const minHeight = (() => {
    const defaultMinHeight = 450;
    if (containerRef.current) {
      if (argumentValues.length) {
        /**
         * scroll is not active
         */
        if (argumentValues.length < 6) {
          const percentage = (defaultMinHeight * argumentValues.length * 10) / 100;
          return defaultMinHeight + percentage;
        }

        /**
         * scroll is active
         */
        return containerRef.current.style.minHeight;
      }
    }

    return defaultMinHeight;
  })();

  return (
    <div
      ref={containerRef}
      style={{ minHeight }}
      className="args-base-container func-args-container"
    >
      <div className="radius-container">
        {props.isRecordType ? null : (
          <>
            <div className="header">
              <span className="title">Function Arguments</span>
            </div>

            <div className="arguments-container">
              <div className="header">
                <div className="column-flex-1">
                  <span className="label">Name</span>
                </div>
                <div className="column-flex-1">
                  <span className="label">Description</span>
                </div>
              </div>

              <ul aria-label="arguments-list" className="thin-scroll-bar-style">
                {props.argumentValues.map((item, idx) => {
                  const numberOfLine = idx + 1;
                  const unique = 'arguments-list-item-' + idx;
                  return (
                    <li className="row" key={unique}>
                      <div className="section">
                        <div className="index">{numberOfLine}</div>
                        <TextField
                          name="name"
                          value={item.name}
                          variant="outlined"
                          className="input-arg"
                          placeholder="Apply on attribute"
                          onChange={(event: any) => onChangeField(idx, event)}
                        />
                      </div>
                      <div className="section">
                        <TextField
                          value={item.description}
                          variant="outlined"
                          className="input-arg"
                          name="description"
                          placeholder="Description"
                          onChange={(event: any) => onChangeField(idx, event)}
                        />
                      </div>
                      <button
                        role="button"
                        className="deleted-arg-value"
                        onClick={() => deleteFunctionArgument(idx)}
                      >
                        <Tooltip title="delete">
                          <DeleteIcon fill="#9293A0" width={16} height={16} />
                        </Tooltip>
                      </button>
                    </li>
                  );
                })}
              </ul>

              <div className="plus-one-input">
                <button
                  role="button"
                  onClick={addFunctionArgument}
                  className="plus-one-input-button"
                >
                  <span className="label">
                    <Add width={16} height={16} /> Add function argument
                  </span>
                </button>
              </div>
            </div>

            <div style={{ padding: '8px 8px 0' }}>
              <div className="divider" />
            </div>
          </>
        )}

        <div className="code-selector-container">
          <SupportedLanguageButtons title="Function code" options={options} />

          <button
            role="button"
            name="play-code"
            onClick={props.runTransform}
            disabled={props?.isFetchingOutputSamples}
          >
            <span className="text">Test Transform</span>
            <PlayArrow width={16} height={16} />
          </button>
        </div>

        <div className="inner-padding-container">
          <div className="radius-container">
            <div className="code-container">
              <AutoSizer>
                {({ width, height }) => (
                  <TransformCodeMonacoEditor
                    id="function-args"
                    height={height}
                    width={width}
                    theme="light"
                    value={props.code}
                    onChange={onChangeCode}
                    language={props?.language}
                  />
                )}
              </AutoSizer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FunctionArgs;
